import React, { useMemo, useState, useEffect } from 'react'
import apiClient from '../../config/api'

const ACTION_ENDPOINTS = {
  approve: (id) => `/api/stock/transfers/${id}/complete/`,
  reject: (id) => `/api/stock/transfers/${id}/cancel/`
}

const StockMovementDetail = ({ movement }) => {
  const [transfer, setTransfer] = useState(movement || null)
  const [actionMessage, setActionMessage] = useState({ type: '', text: '' })
  const [actionInFlight, setActionInFlight] = useState(null)

  useEffect(() => {
    setTransfer(movement || null)
    setActionMessage({ type: '', text: '' })
    setActionInFlight(null)
  }, [movement])

  const formatCurrency = (value) =>
    typeof value === 'number'
      ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '$0.00'

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString()
  }

  const items = useMemo(() => (Array.isArray(transfer?.items) ? transfer.items : []), [transfer])

  const handleBack = () => {
    const event = new CustomEvent('navigateToScreen', { detail: { screen: 'stock-movement' } })
    window.dispatchEvent(event)
  }

  const renderBadgeClass = () => {
    if (!transfer) return 'bg-secondary'
    const status = transfer.status || transfer.status_display || ''
    if (typeof status === 'string' && status.toLowerCase() === 'completed') {
      return 'bg-success'
    }
    if (typeof status === 'string' && status.toLowerCase() === 'pending') {
      return 'bg-warning text-dark'
    }
    return 'bg-secondary'
  }

  const isCompleted = (data) => {
    const status = data?.status || data?.status_display
    return typeof status === 'string' && status.toLowerCase() === 'completed'
  }

  const handleAction = async (action) => {
    if (!transfer?.id || !ACTION_ENDPOINTS[action]) return
    setActionInFlight(action)
    setActionMessage({ type: '', text: '' })

    try {
      const response = await apiClient.post(ACTION_ENDPOINTS[action](transfer.id))
      if (response?.data) {
        setTransfer(response.data)
      } else if (action === 'approve') {
        setTransfer(prev => prev ? { ...prev, status: 'completed', status_display: 'Completed' } : prev)
      } else if (action === 'reject') {
        setTransfer(prev => prev ? { ...prev, status: 'cancelled', status_display: 'Cancelled' } : prev)
      }
      setActionMessage({
        type: 'success',
        text: action === 'approve' ? 'Transfer approved successfully.' : 'Transfer rejected successfully.'
      })
    } catch (error) {
      console.error(`Failed to ${action} transfer`, error)
      const apiError = error?.response?.data
      setActionMessage({
        type: 'danger',
        text: apiError?.detail || apiError?.message || 'Something went wrong. Please try again.'
      })
    } finally {
      setActionInFlight(null)
    }
  }

  return (
    <>
      <div className="pagetitle d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h1>Stock Movement Detail</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item">Products</li>
              <li className="breadcrumb-item">Stock Movement</li>
              <li className="breadcrumb-item active">Detail</li>
            </ol>
          </nav>
        </div>
        <button className="btn btn-outline-primary" onClick={handleBack}>
          <i className="bi bi-arrow-left me-2"></i>
          Back to List
        </button>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                {!transfer ? (
                  <div className="alert alert-info mb-0">
                    No stock movement was selected. Please go back to the list and pick a transfer to view its details.
                  </div>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                      <div>
                        <h5 className="card-title mb-1">
                          <i className="bi bi-arrow-left-right me-2"></i>
                          {transfer.reference_number || 'Transfer Details'}
                        </h5>
                        <p className="text-muted mb-0">
                          Created {formatDate(transfer.created_at)} by {transfer.created_by_email || 'N/A'}
                        </p>
                      </div>
                      <span className={`badge ${renderBadgeClass()} px-3 py-2`}>
                        {transfer.status_display || transfer.status || 'Unknown'}
                      </span>
                    </div>

                    {!isCompleted(transfer) && (
                      <div className="d-flex gap-2 flex-wrap mb-4">
                        <button
                          className="btn btn-success"
                          onClick={() => handleAction('approve')}
                          disabled={!!actionInFlight}
                        >
                          {actionInFlight === 'approve' ? (
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          ) : (
                            <i className="bi bi-check-circle me-2"></i>
                          )}
                          Approve Transfer
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => handleAction('reject')}
                          disabled={!!actionInFlight}
                        >
                          {actionInFlight === 'reject' ? (
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          ) : (
                            <i className="bi bi-x-circle me-2"></i>
                          )}
                          Reject Transfer
                        </button>
                      </div>
                    )}

                    {actionMessage.text && (
                      <div className={`alert alert-${actionMessage.type} mb-4`} role="alert">
                        {actionMessage.text}
                      </div>
                    )}

                    <div className="row g-3 mb-4">
                      <div className="col-md-4">
                        <div className="border rounded p-3 h-100">
                          <small className="text-muted d-block">Transfer Type</small>
                          <strong>{transfer.transfer_type_display || transfer.transfer_type || 'N/A'}</strong>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="border rounded p-3 h-100">
                          <small className="text-muted d-block">Total Cost</small>
                          <strong>{formatCurrency(Number(transfer.total_cost) || 0)}</strong>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="border rounded p-3 h-100">
                          <small className="text-muted d-block">Reference #</small>
                          <strong>{transfer.reference_number || 'N/A'}</strong>
                        </div>
                      </div>
                    </div>

                    <div className="row g-3 mb-4">
                      <div className="col-md-6">
                        <div className="border rounded p-3 h-100">
                          <h6 className="fw-semibold mb-3">Source</h6>
                          <p className="mb-1"><strong>Warehouse:</strong> {transfer.source_warehouse_name || 'N/A'}</p>
                          {transfer.source_branch_name && (
                            <p className="mb-0"><strong>Branch:</strong> {transfer.source_branch_name}</p>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="border rounded p-3 h-100">
                          <h6 className="fw-semibold mb-3">Destination</h6>
                          <p className="mb-1"><strong>Warehouse:</strong> {transfer.destination_warehouse_name || 'N/A'}</p>
                          {(transfer.destination_branch_name || transfer.destination_branch_name === '') && (
                            <p className="mb-0"><strong>Branch:</strong> {transfer.destination_branch_name || 'N/A'}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row g-3 mb-4">
                      <div className="col-md-6">
                        <div className="border rounded p-3 h-100">
                          <p className="mb-1"><strong>Status Notes:</strong> {transfer.notes || 'N/A'}</p>
                          {transfer.completed_at && (
                            <p className="mb-0">
                              <strong>Completed:</strong> {formatDate(transfer.completed_at)} by {transfer.completed_by_email || transfer.completed_by || 'N/A'}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="border rounded p-3 h-100">
                          <p className="mb-2"><strong>Created By:</strong> {transfer.created_by_email || 'N/A'}</p>
                          <p className="mb-0"><strong>Created At:</strong> {formatDate(transfer.created_at)}</p>
                        </div>
                      </div>
                    </div>

                    <h6 className="fw-semibold mb-3">Items</h6>
                    {items.length === 0 ? (
                      <p className="text-muted">No item details available.</p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-striped">
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>SKU</th>
                              <th className="text-end">Quantity</th>
                              <th className="text-end">Purchase Price</th>
                              <th className="text-end">Total Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, index) => (
                              <tr key={`${item.product_sku || item.product_name || 'item'}-${index}`}>
                                <td>{item.product_name || 'N/A'}</td>
                                <td>{item.product_sku || 'N/A'}</td>
                                <td className="text-end">{item.quantity || 0}</td>
                                <td className="text-end">{formatCurrency(Number(item.purchase_price) || 0)}</td>
                                <td className="text-end">{formatCurrency(Number(item.total_cost) || 0)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default StockMovementDetail

